import { rawString } from '../Language/Language';

export type DayHours = {
  day: string | null;
  hours: string | null;
};

type Group = {
  startDay: string | null;
  endDay: string | null;
  hoursRange: string;
};

const DAYS_ORDER = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'] as const;

export const formatTime24To12 = ({ time24 }: { time24?: string }): string => {
  if (!time24 || time24.toLowerCase() === 'closed') return 'closed';

  const hour = parseInt(time24.slice(0, 2), 10);
  const minute = time24.slice(2);
  const suffix = hour >= 12 ? 'pm' : 'am';
  const hour12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;

  return `${hour12}:${minute}${suffix}`;
};

export const parseRange = (range: string | null): string => {
  if (!range || range.toLowerCase() === 'closed') return 'closed';
  const [start, end] = range.toLowerCase().split('-');
  return `${formatTime24To12({ time24: start })} - ${formatTime24To12({ time24: end })}`;
};

const dayRangeString = (startDay: string | null, endDay: string | null) =>
  startDay === endDay
    ? `{{${startDay}}}`.toLowerCase()
    : `{{${startDay}}} - {{${endDay}}}`.toLowerCase();

const converted = ({ sortedDayHours }: { sortedDayHours: DayHours[] }) =>
  sortedDayHours.map(({ day, hours }) => ({
    day,
    hoursRange: parseRange(hours),
  }));

const grouped = ({ sortedDayHours }: { sortedDayHours: DayHours[] }) =>
  converted({ sortedDayHours }).reduce<Group[]>((acc, { day, hoursRange }) => {
    if (!acc.length) {
      acc.push({ startDay: day, endDay: day, hoursRange });
    } else {
      const lastGroup = acc[acc.length - 1];
      if (lastGroup?.hoursRange === hoursRange) {
        lastGroup.endDay = day;
      } else {
        acc.push({ startDay: day, endDay: day, hoursRange });
      }
    }
    return acc;
  }, []);

const sortedDayHours = ({ dayHoursList }: { dayHoursList: DayHours[] }) =>
  [...dayHoursList].sort(
    (a, b) =>
      DAYS_ORDER.indexOf(a.day as (typeof DAYS_ORDER)[number]) -
      DAYS_ORDER.indexOf(b.day as (typeof DAYS_ORDER)[number]),
  );

export const languageDayTokens: Record<string, string> = {
  closed: rawString({ langKey: `pdx.day.closed` }) ?? '',
  ...Object.fromEntries(
    DAYS_ORDER.map((day): [string, string] => [
      day,
      rawString({ langKey: `pdx.day.short.${day}` }) ?? '',
    ]),
  ),
};

export const formatBusinessHours = ({
  dayHoursList,
}: {
  dayHoursList: DayHours[];
}): string =>
  grouped({
    sortedDayHours: sortedDayHours({
      dayHoursList: dayHoursList,
    }),
  })
    .map((group) => {
      const dayPart = dayRangeString(group.startDay, group.endDay);
      return group.hoursRange === 'closed'
        ? `${dayPart} {{closed}}`
        : `${dayPart} ${group.hoursRange}`;
    })
    .join('; ');

export const hoursToString = ({
  locationHours,
}: {
  locationHours?: DayHours[] | null;
}) => {
  const hours = locationHours
    ? formatBusinessHours({ dayHoursList: locationHours })
    : null;

  const formattedHours = rawString({
    content: hours ?? '',
    replacementTokens: languageDayTokens ?? {},
  });

  return formattedHours;
};
